import { useEffect, useState } from "react";

/**
 * The marketing site's tiny API client.
 *
 * The dashboard has a generated client; the public pages read a handful of
 * unauthenticated endpoints and hand-write their types instead (see
 * publicPages.ts and stationStatus.ts), which keeps this bundle small.
 */

/** Thrown when the path names something that doesn't exist. */
export class NotFoundError extends Error {
  constructor(path: string) {
    super(`Not found: ${path}`);
    this.name = "NotFoundError";
  }
}

export const fetchJson = async <T>(
  path: string,
  signal?: AbortSignal,
): Promise<T> => {
  const response = await fetch(`/api/v1/${path}`, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (response.status === 404) {
    throw new NotFoundError(path);
  }
  if (!response.ok) {
    throw new Error(`${path} failed: ${response.status}`);
  }
  return (await response.json()) as T;
};

export type FetchState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  /** The thing doesn't exist — a 404 page, not a retry. */
  | { kind: "missing" }
  | { kind: "error" };

/** Reads `path` once. For anything that needs refreshing, see useStationStatus. */
export const usePageData = <T>(path: string): FetchState<T> => {
  const [state, setState] = useState<FetchState<T>>({ kind: "loading" });

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    setState({ kind: "loading" });

    fetchJson<T>(path, controller.signal)
      .then((data) => {
        if (!disposed) {
          setState({ kind: "ready", data });
        }
      })
      .catch((error: Error) => {
        if (disposed || error?.name === "AbortError") {
          return;
        }
        setState(
          error instanceof NotFoundError
            ? { kind: "missing" }
            : { kind: "error" },
        );
      });

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [path]);

  return state;
};

/** Thrown on a non-2xx POST; `fields` carries DRF's per-field messages. */
export class RequestFailedError extends Error {
  fields: Record<string, string[]>;

  constructor(path: string, status: number, fields: Record<string, string[]>) {
    super(`${path} failed: ${status}`);
    this.name = "RequestFailedError";
    this.fields = fields;
  }

  /** The first message for `field`, or undefined. */
  fieldError(field: string): string | undefined {
    return this.fields[field]?.[0];
  }
}

export const postJson = async <T = unknown>(
  path: string,
  body: unknown,
): Promise<T | undefined> => {
  const response = await fetch(`/api/v1/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let fields: Record<string, string[]> = {};
    try {
      fields = (await response.json()) as Record<string, string[]>;
    } catch {
      // A gateway error page isn't JSON; the status alone will do.
    }
    throw new RequestFailedError(path, response.status, fields);
  }
  if (response.status === 204) {
    return undefined;
  }
  return (await response.json()) as T;
};

/** Path segments come from the URL bar; never interpolate them raw. */
export const seg = (value: string) => encodeURIComponent(value);
